import { Card } from '../ui'
import { DatePlate } from './DatePlate'
import { days, placeName, type Period } from '../lib/calendar'
import { fmtDayShort } from '../lib/today'
import type { Visitor } from '../lib/types'
import { homeName, useRegionsNow } from '../lib/regions'
import { useProfile } from '../lib/profile'

/**
 * A visitor between two `PeriodCard`s on the month view — gold rather than a
 * place's own hue (Berlin's quiet tint or a trip's border), because this is
 * a person, not somewhere Doug went. Ported from the prototype's
 * `visitorCard`.
 *
 * "N nights · staying at yours", or, where a trip overlaps the visit,
 * "staying at yours while you are in Rome" — computed against the same
 * `periods` list `MonthRibbon`'s caller already has, not re-derived here.
 * Brief rule (Y-shaped, same as the ribbon stripe): a stay that only touches
 * the visit at its very edge — ends the day the visitor arrives, or starts
 * the day they leave — does not count as "away", which is why the overlap
 * check below is strict on both ends.
 */
export function VisitorCard({ visitor, periods, second, onEdit }: {
  visitor: Visitor
  periods: Period[]
  /** The profile's second home, for what a `Home2` period is called. */
  second?: { label: string } | null
  /** Y3 parity: tap to edit, like a stay, a commitment or a flight. */
  onEdit?: () => void
}) {
  const regions = useRegionsNow()
  const profile = useProfile()
  const nights = days(visitor.starts_on, visitor.ends_on)
  const away = periods.filter(p =>
    p.place !== 'Home' && p.end > visitor.starts_on && p.start < visitor.ends_on)
  const elsewhere = away[0] ? placeName(away[0], second, homeName(regions, profile)) : null
  const where = visitor.at_mine
    ? (elsewhere ? `staying at yours while you are in ${elsewhere}` : 'staying at yours')
    : 'staying elsewhere'
  // WP 10.7 (10.5's own follow-up): the start date now has a face — the
  // same `DatePlate` `PeriodCard`'s rows already carry — so the header only
  // needs to say when the visit ends.
  const until = visitor.starts_on === visitor.ends_on
    ? ''
    : `until ${fmtDayShort(visitor.ends_on)}`

  const headInner = (
    <>
      <span className="yr-place">
        <i className="yr-dot" style={{ background: 'var(--p-visit)' }} aria-hidden="true" />
        {visitor.who} visiting
      </span>
      <span className="yr-dates">{until}</span>
    </>
  )

  return (
    <Card className="yr-period yr-period--visitor">
      {/* WP 10.7: `.yr-head` still owns the column's own padding and gap —
          this is its one child now, a plain inline row (no new CSS file is
          in this package's brief) putting the plate beside the text stack
          `.yr-head__btn`/`.yr-len` already were — the same shape
          `PeriodCard.tsx` uses. */}
      <div className="yr-head">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
          <DatePlate on={visitor.starts_on} hue="var(--p-visit)" />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {onEdit ? (
              <button type="button" className="yr-head__btn" onClick={onEdit}>{headInner}</button>
            ) : (
              <div className="yr-head__btn">{headInner}</div>
            )}
            <div className="yr-len">
              <b>{nights} night{nights === 1 ? '' : 's'}</b> · {where}
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
